import React, { useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

interface Position {
  id: number;
  name: string;
  departmentId: number;
}

interface Props {
  positions: Position[];
  departmentId: number;
  fetchPositions: (departmentId: number) => Promise<void>;
  showSnackbar: (message: string, severity: "success" | "error") => void;
  loading: boolean;
  setDeleteTarget: (target: { id: number; type: "position" }) => void;
  setDeleteDialogOpen: (open: boolean) => void;
  setCreateDialogOpen: () => void; // ✅ simplified
  setEditingItem: (item: { id: number; name: string; type: "position" }) => void;
}

const PositionTable: React.FC<Props> = ({
  positions,
  loading,
  setDeleteTarget,
  setDeleteDialogOpen,
  setCreateDialogOpen,
  setEditingItem,
}) => {
  const [search, setSearch] = useState("");

  const filtered = positions.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <TextField
          label="Search Positions"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Tooltip title="Add Position">
          <IconButton onClick={setCreateDialogOpen} color="primary">
            <span className="material-icons">add</span>
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((position) => (
                <TableRow key={position.id} hover>
                  <TableCell>{position.name}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() =>
                          setEditingItem({
                            id: position.id,
                            name: position.name,
                            type: "position",
                          })
                        }
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => {
                          setDeleteTarget({ id: position.id, type: "position" });
                          setDeleteDialogOpen(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No positions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default PositionTable;
